import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useContext,
} from "react";
import { useLocalStorage, useDebouncedState } from "@mantine/hooks";
import UserService from "../services/UserService";
import { AuthContext } from "./AuthContext";

export const UsersContext = createContext(null);

const FILTER_TO_PARAMS = {
  all: {},
  active: { is_active: true },
  inactive: { is_active: false },
  expiring: { expiring: true },
};

export const UsersProvider = ({ children, selectedOrgId: passedOrgId }) => {
  const { selectedOrgId: contextOrgId } = useContext(AuthContext);
  const selectedOrgId = passedOrgId || contextOrgId;
  // When embedded in the Organisation view (an explicit org id is passed in),
  // the list must always be scoped to that org - the global org-scope toggle
  // (persisted for the standalone Users page) must not affect it.
  const forceOrgScoped = !!passedOrgId;

  const [users, setUsers] = useState([]);
  const [usersCount, setUsersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [counts, setCounts] = useState(null);

  const [pagination, setPagination] = useLocalStorage({
    key: "users-pagination",
    defaultValue: { pageIndex: 0, pageSize: 25 },
    getInitialValueInEffect: false,
  });
  const [sorting, setSorting] = useLocalStorage({
    key: "users-sorting",
    defaultValue: [],
    getInitialValueInEffect: false,
  });
  const [columnPinning, setColumnPinning] = useLocalStorage({
    key: "users-column-pinning",
    defaultValue: { left: [], right: ["mrt-row-actions"] },
    getInitialValueInEffect: false,
  });
  const [columnVisibility, setColumnVisibility] = useLocalStorage({
    key: "users-column-visibility",
    defaultValue: {},
    getInitialValueInEffect: false,
  });
  const [columnOrder, setColumnOrder] = useLocalStorage({
    key: "users-column-order",
    defaultValue: [],
    getInitialValueInEffect: false,
  });
  const [filter, setFilterState] = useLocalStorage({
    key: "users-filter",
    defaultValue: "all",
    getInitialValueInEffect: false,
  });
  const [orgScoped, setOrgScopedState] = useLocalStorage({
    key: "users-org-scoped",
    defaultValue: true,
    getInitialValueInEffect: false,
  });
  const [search, setSearch] = useDebouncedState("", 500);

  const setOrgScoped = useCallback(
    (next) => {
      setOrgScopedState(next);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setOrgScopedState, setPagination]
  );

  // Only send the organisation filter to the API when the scope toggle is on.
  const scopedOrgId = forceOrgScoped || orgScoped ? selectedOrgId : undefined;

  const setFilter = useCallback(
    (next) => {
      setFilterState(next);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setFilterState, setPagination]
  );

  const fetchUsers = useCallback(
    async () => {
      setLoading((prev) => (users.length === 0 ? true : prev));
      if (users.length > 0) {
        setIsRefetching(true);
      }

      try {
        const ordering = sorting.length
          ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
          : "";
        const filterParams = FILTER_TO_PARAMS[filter] || {};
        const response = await UserService.getUsers({
          page_size: pagination.pageSize,
          page: pagination.pageIndex + 1,
          search,
          ordering,
          organisation: scopedOrgId,
          ...filterParams,
        });

        setUsers(response.results);
        setUsersCount(response.count);
      } catch (error) {
        // Out-of-range page (a stale persisted page for an org/filter with
        // fewer users) 404s - snap back to the first page, which refetches.
        if (error?.response?.status === 404 && pagination.pageIndex > 0) {
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          return;
        }
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
        setIsRefetching(false);
      }
    },
    [
      pagination.pageSize,
      pagination.pageIndex,
      search,
      sorting,
      users.length,
      scopedOrgId,
      filter,
    ]
  );

  const fetchCounts = useCallback(async () => {
    const next = await UserService.getUserCounts({
      search,
      organisation: scopedOrgId,
    });
    setCounts(next);
  }, [search, scopedOrgId]);

  const memoizedSorting = useMemo(() => sorting, [JSON.stringify(sorting)]);

  // Reset pageIndex synchronously when the org changes (before any fetch fires),
  // so a high page persisted from a previous org doesn't trigger an
  // out-of-range request - the backend 404s "Invalid Page" for that.
  // `loading` goes up in the same pass: an org switch replaces the whole list
  // rather than refreshing it, so the panes show skeletons instead of leaving
  // the previous org's users on screen until the response lands.
  const [prevOrgId, setPrevOrgId] = useState(selectedOrgId);
  if (selectedOrgId !== prevOrgId) {
    setPrevOrgId(selectedOrgId);
    if (forceOrgScoped || orgScoped) {
      setLoading(true);
      setPagination((prev) =>
        prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
      );
    }
  }

  useEffect(() => {
    fetchUsers();
  }, [
    pagination.pageSize,
    pagination.pageIndex,
    memoizedSorting,
    search,
    fetchUsers,
  ]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const createUser = useCallback(
    async (payload) => {
      try {
        const data = await UserService.createUser(payload);
        fetchUsers();
        fetchCounts();
        return data;
      } catch (error) {
        console.error("Error creating user:", error);
        throw error;
      }
    },
    [fetchUsers, fetchCounts]
  );

  return (
    <UsersContext.Provider
      value={{
        users,
        usersCount,
        loading,
        isRefetching,
        pagination,
        setPagination,
        sorting,
        setSorting,
        search,
        setSearch,
        filter,
        setFilter,
        orgScoped,
        setOrgScoped,
        counts,
        columnPinning,
        setColumnPinning,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        fetchUsers,
        createUser,
        selectedOrgId,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};
